<template>
  <div class="app-container" :class="{ 'admin-page': isAdminPage, 'auth-page': isAuthPage }">
    <Header v-if="!isAuthPage && !isAdminPage" class="header">
      <div class="nav-container">
        <div class="nav-left">
          <Icon type="md-menu" class="mobile-menu-btn" @click="showMobileMenu = true" />
          <div class="brand-wrapper" @click="go('home')" style="cursor: pointer;">
            <img src="@/assets/logo.png" alt="刷题网" class="nav-logo" />
            <span class="brand-name">刷题网</span>
          </div>
        </div>

        <div class="nav-center">
          <Menu mode="horizontal" theme="light" :active-name="activeMenu" @on-select="go">
            <MenuItem name="home">首页</MenuItem>
            <MenuItem name="zujuan">组卷</MenuItem>
            <MenuItem name="papers">试卷库</MenuItem>
            <MenuItem name="question-lists">精选题单</MenuItem>
            <MenuItem name="albums">精品专辑</MenuItem>
            <MenuItem name="activities">活动中心</MenuItem>
            <MenuItem name="forum">学习论坛</MenuItem>
            <MenuItem name="chat">
            消息
            <Badge v-if="totalUnreadCount > 0" :count="totalUnreadCount" :offset="[10, -5]" />
            </MenuItem>
          </Menu>
        </div>

        <div class="nav-right">
          <div v-if="!isLogin" class="auth-buttons">
            <Button type="text" @click="openAuth('login')">登录</Button>
            <Button type="primary" @click="openAuth('register')">注册</Button>
          </div>

          <div v-else class="user-info">
            <Dropdown trigger="click" placement="bottom-end">
              <div class="user-dropdown-trigger">
                <div class="avatar-wrapper">
                  <Avatar :src="user.avatar || defaultAvatar" size="small" />
                  <span v-if="unreadFeedbackCount > 0" class="unread-dot"></span>
                </div>
                <span class="username">{{ user.username }}</span>
                <Icon type="md-arrow-dropdown" />
              </div>
              <template #list>
                <DropdownMenu>
                  <div class="dropdown-user-info" @click="goToPointsCenter" style="cursor: pointer;">
                    <span class="coin-icon">🪙</span> {{ points }} 积分
                  </div>
                  <DropdownItem divided @click="goToUserSpace">我的空间</DropdownItem>
                  <DropdownItem @click="goToPointsCenter">积分中心</DropdownItem>
                  <DropdownItem @click="goToProfile">个人设置</DropdownItem>
                  <DropdownItem @click="goToFeedback">
                    我的反馈
                    <span v-if="unreadFeedbackCount > 0" class="menu-badge">{{ unreadFeedbackCount }}</span>
                  </DropdownItem>

                  <DropdownItem v-if="user.role === 'admin'" divided @click="goToAdmin">管理界面</DropdownItem>

                  <DropdownItem divided @click="logout">退出登录</DropdownItem>
                </DropdownMenu>
              </template>
            </Dropdown>
          </div>
        </div>
      </div>

    </Header>

    <!-- 主要内容区域：根据页面类型渲染，统一移除冗余的 Card 包装 -->
    <div v-if="isAuthPage" class="full-page-content">
      <router-view />
    </div>
    <div v-else class="app-main-content" :class="{
      'no-padding': isZujuanPage || isProfilePage || isPapersPage || isHomePage || isAlbumPage || isChatPage,
      'is-full-width': isHomePage || isChatPage,
      'admin-full-layout': isAdminPage
    }">
      <router-view />
    </div>

    <Modal v-model="showAuth" :footer-hide="true" width="420" class-name="auth-modal">
      <div class="auth-container">
        <div class="auth-header">
          <div class="auth-tabs">
            <span :class="{ active: authMode === 'login' }" @click="authMode = 'login'">登录</span>
            <span :class="{ active: authMode === 'register' }" @click="authMode = 'register'">注册</span>
          </div>
        </div>

        <div v-if="authMode === 'login'" class="auth-form">
          <div class="form-group">
            <label>用户名</label>
            <Input v-model="form.username" placeholder="请输入用户名" prefix="ios-person-outline" size="large" />
          </div>
          <div class="form-group">
            <label>密码</label>
            <Input type="password" v-model="form.password" placeholder="请输入密码" prefix="ios-lock-outline" size="large"
              @keyup.enter.native="login" />
          </div>
          <Button type="primary" long size="large" :loading="loading" @click="login">登录</Button>
          <div class="auth-footer">
            <span>还没有账号？</span>
            <a @click="authMode = 'register'">立即注册</a>
          </div>
        </div>

        <div v-if="authMode === 'register'" class="auth-form">
          <div class="form-group">
            <label>用户名</label>
            <Input v-model="registerForm.username" placeholder="请输入用户名" prefix="ios-person-outline" size="large" />
          </div>
          <div class="form-group">
            <label>密码</label>
            <Input type="password" v-model="registerForm.password" placeholder="请设置密码（至少6位）" prefix="ios-lock-outline"
              size="large" />
          </div>
          <div class="form-group">
            <label>确认密码</label>
            <Input type="password" v-model="registerForm.confirmPassword" placeholder="请再次输入密码"
              prefix="ios-lock-outline" size="large" @keyup.enter.native="register" />
          </div>
          <Button type="primary" long size="large" :loading="loading" @click="register">注册</Button>
          <div class="auth-footer">
            <span>已有账号？</span>
            <a @click="authMode = 'login'">立即登录</a>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Mobile Navigation Drawer -->
    <Drawer v-model="showMobileMenu" placement="left" :closable="false" class-name="mobile-nav-drawer" width="280">

      <div class="mobile-drawer-header">
        <div class="brand-wrapper mobile-drawer-brand" @click="go('home')" style="cursor: pointer;">
          <img src="@/assets/logo.png" alt="刷题网" class="nav-logo" />
          <span class="brand-name">刷题网</span>
        </div>
      </div>

      <div class="mobile-drawer-user">
        <div v-if="!isLogin" class="mobile-auth-buttons">
          <Button type="primary" ghost @click="openAuth('login')" long style="margin-bottom: 10px;">登录</Button>
          <Button type="primary" @click="openAuth('register')" long>注册</Button>
        </div>
        <div v-else class="mobile-user-card">
          <Avatar :src="user.avatar || defaultAvatar" size="large" />
          <div class="mobile-user-details">
            <span class="mobile-username">{{ user.username }}</span>
            <span class="mobile-coin" @click="handleMobileNav('points')"><span class="coin-icon">🪙</span> {{ points }}
              积分</span>
          </div>
        </div>
      </div>

      <Menu mode="vertical" theme="light" :active-name="activeMenu" @on-select="handleMobileNav" width="auto">
        <MenuItem name="home">
        <Icon type="md-home" />首页</MenuItem>
        <MenuItem name="zujuan">
        <Icon type="md-list-box" />组卷</MenuItem>
        <MenuItem name="papers">
        <Icon type="md-document" />试卷库</MenuItem>
        <MenuItem name="question-lists">
        <Icon type="md-star" />精选题单</MenuItem>
        <MenuItem name="albums">
        <Icon type="md-albums" />精品专辑</MenuItem>
        <MenuItem name="activities">
        <Icon type="md-trophy" />活动中心</MenuItem>
        <MenuItem name="forum">
        <Icon type="md-chatbubbles" />学习论坛</MenuItem>
        <MenuItem name="chat">
        <Icon type="md-chatboxes" />消息
        <Badge v-if="totalUnreadCount > 0" :count="totalUnreadCount" class="mobile-menu-badge" />
        </MenuItem>

        <MenuGroup title="个人中心" v-if="isLogin">
          <MenuItem name="profile-space">
          <Icon type="md-person" />我的空间</MenuItem>
          <MenuItem name="points">
          <Icon type="md-coin" />积分中心</MenuItem>
          <MenuItem name="profile-center">
          <Icon type="md-settings" />个人设置</MenuItem>
          <MenuItem name="feedback">
          <Icon type="md-help-circle" />我的反馈
          <Badge v-if="unreadFeedbackCount > 0" :count="unreadFeedbackCount" class="mobile-menu-badge" />
          </MenuItem>
          <MenuItem name="admin" v-if="user.role === 'admin'">
          <Icon type="md-key" />管理界面</MenuItem>
          <MenuItem name="logout" style="color: #ed4014;">
          <Icon type="md-log-out" />退出登录</MenuItem>
        </MenuGroup>
      </Menu>
    </Drawer>
  </div>
</template>

<script>
export default {
  name: "NavBar",
  data() {
    return {
      showMobileMenu: false,
      showAuth: false,
      authMode: 'login',
      loading: false,
      unreadFeedbackCount: 0,
      totalUnreadCount: 0,
      points: 0,
      // 💥 默认头像地址已设置 💥
      defaultAvatar: "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png",
      form: { username: "", password: "" },
      registerForm: { username: "", password: "", confirmPassword: "" },
    };
  },
  created() {
    // Authentication is now handled by Vuex store which initializes from localStorage
    this.pollUnreadFeedbacks()
    this.pollChatUnreadCount()
    this.pollPointsBalance()
  },
  computed: {
    isAdminPage() {
      return this.$route.path.startsWith('/admin');
    },
    isAuthPage() {
      return this.$route.path === '/login' || this.$route.path === '/register' || this.$route.name === 'Login' || this.$route.name === 'Register';
    },
    isLogin() {
      return !!this.$store.state.token;
    },
    user() {
      return this.$store.state.user || { username: '', role: 'user', avatar: this.defaultAvatar };
    },
    isHomePage() {
      return this.$route.path === '/' || this.$route.path === '/home';
    },
    isZujuanPage() {
      return this.$route.path === '/zujuan' || this.$route.name === 'Zujuan';
    },
    isProfilePage() {
      return this.$route.path === '/profile';
    },
    isPapersPage() {
      return this.$route.path === '/papers' || this.$route.path === '/paper-list';
    },
    isAlbumPage() {
      return this.$route.path === '/albums' || this.$route.path.startsWith('/albums/');
    },
    isChatPage() {
      return this.$route.path === '/chat' || this.$route.name === 'Chat';
    },
    activeMenu() {
      const path = this.$route.path;
      if (path === '/' || path === '/home') return 'home';
      if (path === '/zujuan' || path === '/paper' || path.startsWith('/paper/')) return 'zujuan';
      if (path === '/papers' || path === '/paper-list' || path.startsWith('/papers/')) return 'papers';
      if (path === '/albums' || path.startsWith('/albums/')) return 'albums';
      if (path === '/activities' || path.startsWith('/activities/')) return 'activities';
      if (path === '/question-lists' || path.startsWith('/question-lists/')) return 'question-lists';
      if (path === '/forum' || path.startsWith('/forum/') || path.startsWith('/checkins/')) return 'forum';
      if (path === '/chat' || path.startsWith('/chat')) return 'chat';
      return 'home';
    }
  },
  methods: {
    openAuth(mode) {
      this.authMode = mode;
      this.showAuth = true;
      this.showMobileMenu = false; // Close drawer if open
    },
    go(name) {
      if (!name) return;
      this.showMobileMenu = false; // Always close mobile menu on navigation

      const routeMap = {
        home: "/",
        zujuan: "/zujuan",
        papers: "/papers",
        "question-lists": "/question-lists",
        albums: "/albums",
        activities: "/activities",
        forum: "/forum",
        chat: "/chat",
      };

      if (routeMap[name]) {
        if (this.$route.path !== routeMap[name]) {
          this.$router.push(routeMap[name]);
        }
      }
    },
    handleMobileNav(name) {
      this.showMobileMenu = false; // Close drawer
      if (name === 'logout') {
        this.logout();
      } else if (name === 'profile-space') {
        this.goToUserSpace();
      } else if (name === 'profile-center') {
        this.goToProfile();
      } else if (name === 'points') {
        this.goToPointsCenter();
      } else if (name === 'feedback') {
        this.goToFeedback();
      } else if (name === 'admin') {
        this.goToAdmin();
      } else {
        this.go(name);
      }
    },
    async login() {
      if (!this.form.username || !this.form.password) {
        this.$Message.error("请输入用户名和密码");
        return;
      }

      this.loading = true;
      try {
        const response = await fetch("/api/login", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(this.form),
        });

        const data = await response.json();

        if (response.ok) {
          this.$Message.success("登录成功");
          this.isLogin = true;

          this.$store.commit('SET_USER', {
            ...data.user,
            avatar: data.user.avatar || this.defaultAvatar,
            role: data.user.role || 'user',
          });
          this.$store.commit('SET_TOKEN', data.token);

          this.showAuth = false;
          this.form = { username: "", password: "" };
        } else {
          this.$Message.error(data.error || "登录失败");
        }
      } catch (error) {
        console.error("Login error:", error);
        this.$Message.error("登录出错");
      } finally {
        this.loading = false;
      }
    },
    async register() {
      if (!this.registerForm.username || !this.registerForm.password) {
        this.$Message.error("请输入用户名和密码");
        return;
      }
      if (this.registerForm.password.length < 6) {
        this.$Message.error("密码至少需要6位");
        return;
      }
      if (this.registerForm.password !== this.registerForm.confirmPassword) {
        this.$Message.error("两次输入的密码不一致");
        return;
      }

      this.loading = true;
      try {
        const response = await fetch("/api/register", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            username: this.registerForm.username,
            password: this.registerForm.password,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          this.$Message.success("注册成功，请登录");
          this.authMode = 'login';
          this.form.username = this.registerForm.username;
          this.form.password = "";
          this.registerForm = { username: "", password: "", confirmPassword: "" };
        } else {
          this.$Message.error(data.error || "注册失败");
        }
      } catch (error) {
        console.error("Register error:", error);
        this.$Message.error("注册出错");
      } finally {
        this.loading = false;
      }
    },
    logout() {
      this.$store.commit('CLEAR_AUTH');
      this.$Message.info("已退出登录");
      this.$router.push({ name: "home" });
    },
    goToProfile() {
      // 个人中心 -> 设置页面
      this.$router.push({ name: "UserProfile" });
    },
    goToUserSpace() {
      // 我的空间 -> 公开个人主页
      if (this.user && this.user.id) {
        this.$router.push({ name: "UserSpace", params: { id: this.user.id } });
      } else {
        this.$router.push({ name: "UserProfile" });
      }
    },
    goToAdmin() {
      // 假设路由配置中有名为 'Admin' 的路由
      this.$router.push({ name: "Admin" });
    },
    goToFeedback() {
      this.$router.push({ name: "MyFeedback" });
    },
    async pollUnreadFeedbacks() {
      if (!this.$store.state.token) return;
      try {
        const res = await fetch('/api/feedbacks/unread-count', {
          headers: { 'Authorization': `Bearer ${this.$store.state.token}` }
        });
        if (res.ok) {
          const data = await res.json();
          this.unreadFeedbackCount = data.unreadCount || 0;
        }
      } catch (e) { /* ignore */ }
      // Poll every 60 seconds
      setTimeout(() => this.pollUnreadFeedbacks(), 60000);
    },
    async pollChatUnreadCount() {
      if (!this.$store.state.token) return;
      try {
        const res = await fetch('/api/chat/unread-count', {
          headers: { 'Authorization': `Bearer ${this.$store.state.token}` }
        });
        if (res.ok) {
          const data = await res.json();
          this.totalUnreadCount = data.unreadCount || 0;
        }
      } catch (e) { /* ignore */ }
      // Poll every 30 seconds for chat
      setTimeout(() => this.pollChatUnreadCount(), 30000);
    },
    async pollPointsBalance() {
      if (!this.$store.state.token) return;
      try {
        const res = await fetch('/api/points/balance', {
          headers: { 'Authorization': `Bearer ${this.$store.state.token}` }
        });
        if (res.ok) {
          const data = await res.json();
          this.points = data.total_points || 0;
        }
      } catch (e) { /* ignore */ }
      // Poll every 60 seconds
      setTimeout(() => this.pollPointsBalance(), 60000);
    },
    goToPointsCenter() {
      this.$router.push({ name: 'PointsCenter' });
    },
  },
};
</script>

<style scoped>
/* 保持您提供的所有样式不变，只检查和确认用户下拉菜单相关的样式 */
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* 管理后台页面全屏布局 */
.app-container.admin-page {
  padding: 0;
  margin: 0;
}

/* 管理后台内容区域，占满剩余空间 */
.admin-content {
  margin-top: 64px;
  flex: 1;
  height: calc(100vh - 64px);
  overflow: hidden;
}

/* 首页内容区域 */
.home-content {
  margin-top: 64px;
  flex: 1;
  width: 100%;
}

/* 组卷页面内容区域 */
.zujuan-content {
  margin-top: 64px;
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

/* 固定导航栏样式 */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  /* 改为居中布局 */
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

/* 新增导航容器：确保与主内容完全对齐 */
.nav-container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 32px;
}

@media (max-width: 1440px) {
  .nav-container {
    max-width: 100%;
    padding: 0 24px;
  }
}

.nav-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.brand-wrapper {
  display: flex;
  align-items: center;
}

.mobile-menu-btn {
  display: none;
  font-size: 24px;
  color: #333;
  cursor: pointer;
}

.nav-logo {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: contain;
  margin-right: 12px;
}

.brand-name {
  font-size: 18px;
  font-weight: 800;
  color: #1e3a8a;
  letter-spacing: -0.02em;
  white-space: nowrap;
}

/* 新增中间导航区域样式 */
.nav-center {
  flex-grow: 1;
  display: flex;
  justify-content: flex-start;
  /* 从 center 改为 flex-start */
}

/* 调整主内容区域最大宽度匹配 */
.main-content {
  margin-top: 64px;
  flex: 1;
  padding: 20px;
  max-width: 1200px;
  /* 新增 */
  width: 100%;
  /* 新增 */
  margin-left: auto;
  /* 新增 */
  margin-right: auto;
  /* 新增 */
}

.content {
  flex: 1;
  padding: 20px;
}

/* 响应式布局：调低断点以支持 iPad mini (1024px) 显示全导航栏 */
@media (max-width: 1023px) {

  .nav-center,
  .nav-right {
    display: none;
  }

  .mobile-menu-btn {
    display: block;
  }

  .nav-left MenuItem {
    font-size: 14px;
  }
}

/* 针对中等屏幕（iPad mini 1024px 到 窄屏电脑 1280px）的极限紧凑模式 */
@media (min-width: 1024px) and (max-width: 1280px) {
  .nav-container {
    padding: 0 6px;
    /* 极限缩小两侧边距 */
  }

  /* 在 1024px 到 1180px 之间，隐藏品牌名称文字，确保导航项完整 */
  @media (max-width: 1180px) {
    .brand-name {
      display: none;
    }
  }

  .brand-name {
    font-size: 14px;
  }

  .nav-logo {
    margin-right: 4px;
    width: 26px;
    /* 略微缩小 logo */
    height: 26px;
  }

  :deep(.ivu-menu-item) {
    padding: 0 6px !important;
    /* 极限压缩菜单项左右间距 */
    font-size: 12.5px !important;
    /* 精细调整字号 */
  }

  .nav-left {
    gap: 4px;
  }
}

/* Auth Buttons */
.auth-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
}

/* Auth Modal Styles (保持不变) */
/* Main Layout Content Area */
.app-main-content {
  margin-top: 64px;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding: 20px 32px;
  transition: all 0.3s;
  min-height: calc(100vh - 64px);
  width: 100%;
}

.app-main-content.is-full-width {
  max-width: 100% !important;
}

.app-main-content.no-padding {
  padding: 0;
}

.app-main-content.is-full-width {
  max-width: 100%;
}

@media (max-width: 1440px) {
  .app-main-content {
    max-width: 100%;
    padding: 20px 24px;
  }
}

.app-main-content.admin-full-layout {
  padding: 0;
  margin-top: 0;
  max-width: none;
  min-height: 100vh;
}

.auth-container {
  padding: 20px;
}

.auth-header {
  text-align: center;
  margin-bottom: 30px;
}

.auth-tabs {
  display: inline-flex;
  background: #f5f7fa;
  border-radius: 25px;
  padding: 4px;
}

.auth-tabs span {
  padding: 10px 30px;
  cursor: pointer;
  border-radius: 20px;
  font-size: 15px;
  color: #606266;
  transition: all 0.3s;
}

.auth-tabs span.active {
  background: #409eff;
  color: white;
  font-weight: 500;
}

.auth-tabs span:hover:not(.active) {
  color: #409eff;
}

.auth-form {
  padding: 0 10px;
}

.auth-form .form-group {
  margin-bottom: 20px;
}

.auth-form .form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.auth-footer {
  text-align: center;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
  font-size: 14px;
  color: #909399;
}

.auth-footer a {
  color: #409eff;
  cursor: pointer;
  margin-left: 5px;
}

.auth-footer a:hover {
  color: #66b1ff;
}

/* User Dropdown Styles (确保这部分样式正确) */
.user-info {
  display: flex;
  align-items: center;
  height: 100%;
}

.user-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 4px;
  transition: background 0.3s;
  height: 40px;
}

.user-dropdown-trigger:hover {
  background: #f5f7fa;
}

.username {
  font-size: 14px;
  color: #606266;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-user-info {
  padding: 10px 16px;
  font-size: 14px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 5px;
}

.coin-icon {
  font-size: 18px;
}

.avatar-wrapper {
  position: relative;
  display: inline-flex;
}

.unread-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  background: #f56c6c;
  border-radius: 50%;
  border: 2px solid #fff;
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

.menu-badge {
  display: inline-block;
  background: #f56c6c;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 9px;
  padding: 0 5px;
  margin-left: 8px;
}

/* Mobile responsive styles - updated for 1024px support */
@media screen and (max-width: 1023px) {

  .nav-center,
  .nav-right {
    display: none;
  }

  .mobile-menu-btn {
    display: block;
  }
}

/* Mobile Drawer Styles */
.mobile-nav-drawer .ivu-drawer-body {
  padding: 0;
  display: flex;
  flex-direction: column;
}

.mobile-drawer-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.mobile-drawer-brand {
  display: flex;
  align-items: center;
}

.mobile-drawer-user {
  padding: 24px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fafafa;
}

.mobile-user-card {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mobile-user-details {
  display: flex;
  flex-direction: column;
}

.mobile-username {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.mobile-coin {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
}

.mobile-nav-drawer .ivu-menu-vertical {
  width: 100% !important;
  border-right: none;
}

.mobile-nav-drawer .ivu-menu-item {
  display: flex;
  align-items: center;
  font-size: 15px;
  padding: 14px 24px;
}

.mobile-nav-drawer .ivu-menu-item .ivu-icon {
  margin-right: 12px;
  font-size: 18px;
  color: #666;
}

.mobile-menu-badge {
  margin-left: auto;
}
</style>
```